/**
 * useCardList — one page of `GET /api/cards`, the list surface's data source.
 *
 * Unlike `useBoard` / `useDashboard`, this is NOT a fetch-everything-then-
 * filter-in-memory read. Searching, filtering, sorting and paging are all
 * server concerns here, because the list is deliberately unfiltered by the
 * Done-visibility window and so has an unbounded all-time tail behind it.
 * Every param below goes on the wire; nothing is re-derived on the client.
 *
 * `placeholderData: keepPreviousData` keeps the current page rendered while
 * the next one loads, which is what lets the table dim its body instead of
 * collapsing to a skeleton on every sort or page change.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type {
  CardListColumnName,
  CardListData,
  CardListSortKey,
  CardListSortOrder,
  DeliverableKind,
} from '@/api/types-bridge'

export interface CardListParams {
  /** Pins the list to one project. Omit for the tenant-wide master surface. */
  readonly projectId?: string
  readonly page: number
  readonly pageSize: number
  readonly sort: CardListSortKey
  readonly order: CardListSortOrder
  readonly q?: string
  readonly kinds?: readonly DeliverableKind[]
  readonly columns?: readonly CardListColumnName[]
  /** User ids, and/or the literal `'unassigned'`. */
  readonly assignees?: readonly string[]
}

export const CARD_LIST_QUERY_ROOT = ['card-list'] as const

export const CARD_LIST_QUERY_KEY = (params: CardListParams) =>
  [...CARD_LIST_QUERY_ROOT, params] as const

/**
 * Repeatable filters go on the wire comma-separated — the BE splits on `,`
 * and rejects unknown members rather than silently dropping them.
 */
const csv = (values: readonly string[] | undefined): string | undefined =>
  values && values.length > 0 ? values.join(',') : undefined

export function buildCardListQueryString(params: CardListParams): string {
  const search = new URLSearchParams()
  search.set('page', String(params.page))
  search.set('pageSize', String(params.pageSize))
  search.set('sort', params.sort)
  search.set('order', params.order)

  if (params.projectId) search.set('projectId', params.projectId)
  if (params.q && params.q.length > 0) search.set('q', params.q)

  const kinds = csv(params.kinds)
  if (kinds) search.set('kind', kinds)

  const columns = csv(params.columns)
  if (columns) search.set('column', columns)

  const assignees = csv(params.assignees)
  if (assignees) search.set('assignees', assignees)

  return search.toString()
}

export function useCardList(params: CardListParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: CARD_LIST_QUERY_KEY(params),
    queryFn: async () => {
      const res = await apiClient<{ data: CardListData }>(
        `/api/cards?${buildCardListQueryString(params)}`,
      )
      return res.data
    },
    enabled: options?.enabled ?? true,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
}
